import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const RecommendationDetailsModal = ({ recommendation, isOpen, onClose }) => {
  if (!isOpen || !recommendation) return null;

  const getIconColor = () => {
    switch (recommendation?.variant) {
      case 'aggressive':
        return 'var(--color-success)';
      case 'controlled':
        return 'var(--color-primary)';
      case 'hold':
        return 'var(--color-warning)';
      case 'restructure':
        return 'var(--color-destructive)';
      default:
        return 'var(--color-foreground)';
    }
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm">
      <div className="bg-card rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border p-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div 
              className="w-14 h-14 rounded-xl flex items-center justify-center"
              style={{ backgroundColor: `${getIconColor()}15` }}
            >
              <Icon name={recommendation?.icon} size={28} color={getIconColor()} />
            </div>
            <div>
              <h2 className="text-2xl font-semibold">{recommendation?.title}</h2>
              <p className="text-sm text-muted-foreground">{recommendation?.description}</p>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <Icon name="X" size={24} />
          </Button>
        </div>

        <div className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 rounded-lg bg-muted/50">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="DollarSign" size={20} color="var(--color-primary)" />
                <span className="text-sm font-semibold">Investasi Diperlukan</span>
              </div>
              <p className="text-lg font-bold data-text">{recommendation?.investmentRequired}</p>
            </div>

            <div className="p-4 rounded-lg bg-muted/50">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="Clock" size={20} color="var(--color-primary)" />
                <span className="text-sm font-semibold">Timeline Implementasi</span>
              </div>
              <p className="text-lg font-bold">{recommendation?.timeline}</p>
            </div>

            <div className="p-4 rounded-lg bg-muted/50">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="AlertTriangle" size={20} color="var(--color-primary)" />
                <span className="text-sm font-semibold">Tingkat Risiko</span>
              </div>
              <p className="text-lg font-bold">{recommendation?.riskLevel}</p>
            </div>

            <div className="p-4 rounded-lg bg-muted/50">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="TrendingUp" size={20} color="var(--color-primary)" />
                <span className="text-sm font-semibold">Expected ROI</span>
              </div>
              <p className="text-lg font-bold">{recommendation?.expectedROI}</p>
            </div>
          </div>

          <div className="border-t border-border pt-6">
            <h3 className="text-lg font-semibold mb-4">Hasil yang Diharapkan</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {recommendation?.expectedOutcomes?.map((outcome, index) => (
                <div key={index} className="flex items-start gap-3 p-3 rounded-lg bg-success/5">
                  <Icon name="CheckCircle2" size={20} color="var(--color-success)" className="flex-shrink-0 mt-0.5" />
                  <span className="text-sm">{outcome}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="border-t border-border pt-6">
            <h3 className="text-lg font-semibold mb-4">Faktor Kesuksesan Kritis</h3>
            <div className="space-y-3">
              {recommendation?.criticalSuccessFactors?.map((factor, index) => (
                <div key={index} className="flex items-start gap-3 p-4 rounded-lg border border-border">
                  <Icon name="Target" size={20} color="var(--color-primary)" className="flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <h4 className="text-sm font-semibold mb-1">{factor?.title}</h4>
                    <p className="text-sm text-muted-foreground">{factor?.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="border-t border-border pt-6">
            <h3 className="text-lg font-semibold mb-4">Persyaratan Implementasi</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {recommendation?.implementationRequirements?.map((req, index) => (
                <div key={index} className="p-4 rounded-lg bg-muted/50">
                  <div className="flex items-center gap-2 mb-2">
                    <Icon name={req?.icon} size={18} color="var(--color-primary)" />
                    <span className="text-sm font-semibold">{req?.category}</span>
                  </div>
                  <ul className="space-y-1">
                    {req?.items?.map((item, idx) => (
                      <li key={idx} className="text-xs text-muted-foreground flex items-start gap-2">
                        <span className="text-primary mt-1">•</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>

          <div className="border-t border-border pt-6">
            <div className="p-4 rounded-lg bg-primary/5 border border-primary/20">
              <div className="flex items-start gap-3">
                <Icon name="Brain" size={24} color="var(--color-primary)" className="flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="text-sm font-semibold text-primary mb-2">Analisis AI</h4>
                  <p className="text-sm text-muted-foreground mb-3">{recommendation?.aiAnalysis}</p>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">Confidence Score:</span>
                    <div className="flex items-center gap-2">
                      <div className="w-32 h-2 bg-card rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-primary transition-all duration-500"
                          style={{ width: `${recommendation?.confidenceScore}%` }}
                        />
                      </div>
                      <span className="text-sm font-semibold data-text text-primary">
                        {recommendation?.confidenceScore}%
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="sticky bottom-0 bg-card border-t border-border p-6">
          <Button variant="outline" fullWidth onClick={onClose}>
            Tutup Detail
          </Button>
        </div>
      </div>
    </div>
  );
};

export default RecommendationDetailsModal;